import { RichTextEditorControls } from '@components/utility/richTexts';
import { Box, InputLabel, InputLabelProps, Typography, useTheme } from '@mui/material';
import { EditorOptions } from '@tiptap/react';
import { handleError, isAllowedImage } from '@utils/common';
import { stripColorStyles } from '@utils/richTextEditor';
import {
    ImageNodeAttributes,
    LinkBubbleMenu,
    MenuBarProps,
    RichTextContentProps,
    RichTextField,
    useRichTextEditorContext,
} from 'mui-tiptap';
import { useCallback, useEffect, useRef, useState } from 'react';

interface IRichTextEditorProps {
    label?: string;
    InputLabelProps?: InputLabelProps;
    error?: boolean;
    helperText?: string;
    hasValue?: boolean;
    handleNewImageFiles?: (files: File[], position?: number) => ImageNodeAttributes[] | Promise<ImageNodeAttributes[]>;
    richTextContentProps?: Partial<RichTextContentProps>;
    menuBarProps?: Partial<MenuBarProps>;
}

const RichTextEditor = ({
    error,
    helperText,
    label,
    InputLabelProps,
    hasValue,
    handleNewImageFiles,
    richTextContentProps,
    menuBarProps,
}: IRichTextEditorProps) => {
    const [isFocused, setIsFocused] = useState(false);
    const theme = useTheme();
    const isLabelShrinked = !!InputLabelProps?.shrink || hasValue || isFocused;
    const editor = useRichTextEditorContext();

    // Stable refs to avoid re-registering handlers
    const handleNewImageFilesRef = useRef(handleNewImageFiles);
    useEffect(() => {
        handleNewImageFilesRef.current = handleNewImageFiles;
    }, [handleNewImageFiles]);

    const validateImages = (files: File[]) => {
        if (!files.every(isAllowedImage)) {
            handleError('Error uploading images', { msg: 'Unsupported files detected' });
            return false;
        }
        if (!files.every((file) => file.size <= 5 * 1024 * 1024)) {
            handleError('Error uploading images', { msg: 'Images must be 5MB or less' });
            return false;
        }
        return true;
    };

    // Allow for dropping images into the editor
    const handleDrop: NonNullable<EditorOptions['editorProps']['handleDrop']> = useCallback((view, event) => {
        const cb = handleNewImageFilesRef.current;
        if (!(event instanceof DragEvent) || !event.dataTransfer || !cb) {
            return false;
        }
        const imageFiles = Array.from(event.dataTransfer.files);
        if (!validateImages(imageFiles)) return false;
        if (imageFiles.length > 0) {
            const insertPosition = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
            })?.pos;
            cb(imageFiles, insertPosition);
            event.preventDefault();
            return true;
        }

        return false;
    }, []);

    // Allow for pasting images
    const handlePaste: NonNullable<EditorOptions['editorProps']['handlePaste']> = useCallback((_view, event) => {
        if (!editor?.isEditable) {
            event.preventDefault();
            return true;
        }

        const cb = handleNewImageFilesRef.current;
        if (!event.clipboardData || !cb) {
            return false;
        }

        const pastedImageFiles = Array.from(event.clipboardData.files);
        if (!validateImages(pastedImageFiles)) return false;
        if (pastedImageFiles.length > 0) {
            cb(pastedImageFiles);
            return true;
        }

        // Strip color information from pasted HTML
        const html = event.clipboardData.getData('text/html');
        if (html) {
            const cleanedHtml = stripColorStyles(html);
            if (cleanedHtml !== html) {
                event.preventDefault();
                editor.commands.insertContent(cleanedHtml);
                return true;
            }
        }

        // We return false here to allow the standard paste-handler to run.
        return false;
    }, [editor]);

    // Register focus/blur listeners once
    useEffect(() => {
        if (!editor) return;
        const onFocus = () => setIsFocused(true);
        const onBlur = () => setIsFocused(false);
        editor.on('focus', onFocus);
        editor.on('blur', onBlur);
        return () => {
            editor.off('focus', onFocus);
            editor.off('blur', onBlur);
        };
    }, [editor]);

    useEffect(() => {
        if (!editor) return;
        editor.setOptions({
            editorProps: {
                handleDrop,
                handlePaste,
            },
        });
    }, [editor, handleDrop, handlePaste]);

    const handleLabelClick = useCallback(() => editor?.commands.focus(), [editor]);

    return (
        <Box
            sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiTiptap-FieldContainer-disabled': {
                    '.MuiTiptap-FieldContainer-notchedOutline': {
                        borderColor: '#00000042 !important',
                    },
                },
                '& .MuiTiptap-FieldContainer-root': {
                    '&:hover .MuiTiptap-FieldContainer-notchedOutline': {
                        borderColor: '#005FBE80',
                    },
                    '.MuiTiptap-FieldContainer-notchedOutline': {
                        borderColor: error ? 'red' : '',
                    },
                    '&.MuiTiptap-FieldContainer-focused > .MuiTiptap-FieldContainer-notchedOutline': {
                        borderColor: '#005FBE',
                    },
                },
                position: 'relative',
            }}
            data-error={error ? helperText : undefined}
        >
            <InputLabel
                {...InputLabelProps}
                shrink={isLabelShrinked}
                focused={isFocused}
                sx={{
                    position: 'absolute',
                    zIndex: 10,
                    background: theme.palette.common.white,
                    transform: isLabelShrinked ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, 250%) scale(1)',
                    cursor: 'text',
                    paddingX: isLabelShrinked ? 1 : 0,
                }}
                onClick={handleLabelClick}
            >
                {label}
            </InputLabel>
            <RichTextField
                controls={<RichTextEditorControls onUploadFiles={handleNewImageFiles} />}
                RichTextContentProps={{
                    ...richTextContentProps,
                    classes: {
                        root: 'max-h-[1000px] overflow-auto',
                        readonly: 'bg-anti-flash-white text-black-38 grayscale [&_img]:opacity-50',
                        ...(richTextContentProps?.classes || {}),
                    },
                }}
                MenuBarProps={{
                    ...menuBarProps,
                    classes: {
                        root: '!overflow-auto',
                        ...(menuBarProps?.classes || {}),
                    },
                }}
                className="rounded-lg shadow font-inter"
                sx={{
                    minHeight: '200px',
                    '& .ProseMirror': {
                        minHeight: '200px',
                    },
                }}
            />
            <LinkBubbleMenu />
            <Typography
                fontFamily="Inter"
                fontSize={12}
                className="mt-1"
                style={{ color: error ? theme.palette.error.main : theme.palette.text.disabled }}
            >
                {helperText}
            </Typography>
        </Box>
    );
};

export { RichTextEditor };
